import {
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  Output,
  QueryList,
  TemplateRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { SkeletonListComponent, SkeletonColumn } from '../skeleton-list/skeleton-list.component';
import { EmptyStateComponent } from '../empty-state/empty-state.component';
import { PaginationComponent } from '../pagination/pagination.component';
import { TableColumn } from './table-column.interface';
import { TableCellDirective } from './table-cell.directive';

@Component({
  selector: 'app-data-table',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    SkeletonListComponent,
    EmptyStateComponent,
    PaginationComponent
  ],
  templateUrl: './data-table.component.html',
  styleUrl: './data-table.component.css'
})
export class DataTableComponent {
  @Input() columns: TableColumn[] = [];
  @Input() data: any[] = [];
  @Input() loading: boolean = false;
  @Input() skeletonRows: number = 8;
  @Input() clickableRows: boolean = true;

  // Empty state inputs
  @Input() emptyTitle: string = '';
  @Input() emptyDescription: string = '';
  @Input() emptyIcon: 'empty' | 'search' | 'folder' = 'folder';
  @Input() emptyActionLabel?: string;
  @Input() emptyActionRouterLink?: any[] | string;

  // Pagination inputs
  @Input() showPagination: boolean = false;
  @Input() currentPage: number = 1;
  @Input() totalPages: number = 1;
  @Input() lastPage: boolean = false;
  @Input() firstPage: boolean = true;

  @Output() rowClick = new EventEmitter<any>();
  @Output() emptyAction = new EventEmitter<void>();
  @Output() pageChange = new EventEmitter<number>();

  @ContentChildren(TableCellDirective) cellDirectives!: QueryList<TableCellDirective>;

  getCellTemplate(columnKey: string): TemplateRef<any> | null {
    if (!this.cellDirectives) return null;
    const match = this.cellDirectives.find(d => d.columnKey === columnKey);
    return match ? match.template : null;
  }

  get skeletonColumns(): SkeletonColumn[] {
    return this.columns.map(col => {
      const isAction = col.key === 'action' || col.align === 'center';
      return {
        width: col.width || '100%',
        height: isAction ? '24px' : '16px',
        appearance: isAction ? 'circle' : 'line',
        className: col.align === 'center' ? 'flex justify-center' : 'pr-4',
        theme: {
          width: isAction ? '24px' : '85%',
          height: isAction ? '24px' : '16px'
        }
      };
    });
  }

  onRowClick(row: any, event: MouseEvent): void {
    // Avoid triggering row click if an interactive element inside the row was clicked
    const target = event.target as HTMLElement;
    if (target.closest('button, a, input, select, textarea, [data-no-row-click]')) {
      return;
    }
    if (this.clickableRows) {
      this.rowClick.emit(row);
    }
  }
}
